<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { useStore } from "vuex"

import dayjs from "dayjs"

import PrintFileView from "@/components/order/PrintFileView.vue"
import SwatchColorView from "@/components/order/SwatchColorView.vue"
import Alert from "@/components/shared/Alert.vue"
import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardSubtitle from "@/components/shared/CardSubtitle.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import Icon from "@/components/shared/Icon.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputCheck from "@/components/shared/InputCheck.vue"
import InputRadio from "@/components/shared/InputRadio.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import Modal from "@/components/shared/Modal.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import router from "@/plugins/router"
import purchaseService from "@/services/purchase"
import purchaseProductService from "@/services/purchase-product"
import type { Checked } from "@/types/common.type"
import type { Partner } from "@/types/partner.type"
import type { PurchaseProduct } from "@/types/purchase-product.type"
import type { Address, Purchase } from "@/types/purchase.type"
import { formattedMoney, minimumUnit } from "@/utils/money"
import { reportSentry } from "@/utils/sentry"
import {
  formatByNumber,
  formatLocalDate,
  formattedDate,
  formattedNumber,
  simpleFormatLocalDate,
  unitedNumber,
} from "@/utils/string"

type PurchaseProductInfo = Checked<PurchaseProduct> & {
  //
  pickupAtSelect: string | null
  pickupAtDue: Date | null
  newPickupAtSelect: string | null
  newPickupAt: Date | null
  newPickupAtDue: Date | null

  // confirm
  offPrice: number | null
  priceOffered: boolean
  shipper: string | null

  // reject
  failureReason: string | null
  extendedLeadtime: number | null
  stockQuantity: number | null
  risenUnitPrice: number | null
  productionMoq: number | null
  productionLeadtime: number | null
}

const store = useStore()
const route = useRoute()
const { t } = useI18n()

const partner = computed<Partner | null>(() => store.getters.partner)
const currency = computed(() => {
  return partner.value ? partner.value.tradingCurrency : "usd"
})

const order = ref<Purchase | null>(null)
const shippingAddress = ref<Address | null>(null)
const products = ref<PurchaseProductInfo[]>([])
const productsPending = computed(() => {
  return products.value.filter((e) => e.status === "requested")
})
const productsAccepted = computed(() => {
  return products.value.filter((e) => e.status !== "requested" && e.status !== "failed")
})
const productsRejected = computed(() => {
  return products.value.filter((e) => e.status === "failed")
})
const id = computed(() => route.params.id)
const pageTitle = computed(() => {
  return `${t("order.title")} #${order.value?.code || ""}`
})

const amount = computed(() => {
  return products.value.map((e) => e.price || 0).reduce((t, n) => t + n, 0)
})
const amountAccepted = computed(() => {
  return productsAccepted.value.map((e) => e.price || 0).reduce((t, n) => t + n, 0)
})
const amountRejected = computed(() => {
  return productsRejected.value.map((e) => e.price || 0).reduce((t, n) => t + n, 0)
})

const deliveryMethodOptions = computed(() => [
  { label: t("order.deliveryMethodList.quick"), value: "quick" },
  { label: t("order.deliveryMethodList.logistics"), value: "logistics" },
  // { label: t("order.deliveryMethodOptions.shipping"), value: "shipping" },
])

const pickupAtSelectOptions = ref([
  { label: t("order.pickupAtOptions.today"), value: "today" },
  { label: t("order.pickupAtOptions.tomorrow"), value: "tomorrow" },
  { label: t("order.pickupAtOptions.customDate"), value: "customDate" },
])
const today = ref<Date>(new Date())
const dueStandard = ref<Date | null>(null)
const duePrint = ref<Date | null>(null)
const dueStandardDelayed = ref<Date | null>(null)
const duePrintDelayed = ref<Date | null>(null)

const failureReasonOptions = ref([
  { label: t("order.failureReasonOptions.delayed"), value: "delayed" },
  { label: t("order.failureReasonOptions.outOfStock"), value: "outOfStock" },
  { label: t("order.failureReasonOptions.unitPriceRisen"), value: "unitPriceRisen" },
  { label: t("order.failureReasonOptions.qualityRetired"), value: "qualityRetired" },
  { label: t("order.failureReasonOptions.productRetired"), value: "productRetired" },
])

watchEffect(() => {
  products.value.forEach((product) => {
    const today = dayjs().startOf("day").toDate()
    const tomorrow = dayjs().add(1, "day").startOf("day").toDate()
    if (product.pickupAtSelect === "today") {
      product.pickupAt = today
    } else if (product.pickupAtSelect === "tomorrow") {
      product.pickupAt = tomorrow
    }

    if (product.newPickupAtSelect === "today") {
      product.newPickupAt = today
    } else if (product.newPickupAtSelect === "tomorrow") {
      product.newPickupAt = tomorrow
    }
  })
})

function getDeliveryMethod(supplierType: string, range: string) {
  if (supplierType === "independent") return "shipping"
  else if (supplierType === "subordinate" || supplierType === "subordinate_guangzhou") return "logistics"
  else if (range === "bulk") return "logistics"
  else return "quick"
}

function setPickupAtDue(product: PurchaseProductInfo) {
  if (product.status === "requested" && product.dueDate) {
    if (product.salesType === "standard_order") product.pickupAtDue = new Date(Date.parse(product.dueDate))
    else product.pickupAtDue = new Date(Date.parse(product.dueDate))
  } else if (product.status === "confirmed") {
    if (product.salesType === "standard_order") product.newPickupAtDue = dueStandardDelayed.value
    else product.newPickupAtDue = duePrintDelayed.value
  }
}

async function loadOrder() {
  const base = {
    pickupAtSelect: null,
    pickupAtDue: null,
    newPickupAtSelect: null,
    newPickupAt: null,
    newPickupAtDue: null,
    offPrice: null,
    priceOffered: false,
    shipper: null,
    failureReason: null,
    extendedLeadtime: null,
    stockQuantity: null,
    risenUnitPrice: null,
    productionMoq: null,
    productionLeadtime: null,
  } as PurchaseProductInfo

  const data = await purchaseService.get(Number(id.value))
  order.value = data

  order.value.purchaseProducts.forEach((product) => {
    const productInfo = { ...base, ...product } as PurchaseProductInfo
    if (productInfo.status === "requested") {
      productInfo.deliveryMethod = getDeliveryMethod(partner.value!.supplierType, productInfo.range)
    } else if (productInfo.status === "confirmed") {
      productInfo.newPickupAt = product.pickupAt
    }

    setPickupAtDue(productInfo)

    products.value.push({ ...productInfo, checked: false })
  })

  dueStandard.value = dayjs(new Date(order.value.requestedAt)).add(6, "day").toDate()
  duePrint.value = dayjs(new Date(order.value.requestedAt)).add(13, "day").toDate()
  dueStandardDelayed.value = dayjs(new Date(order.value.requestedAt)).add(20, "day").toDate()
  duePrintDelayed.value = dayjs(new Date(order.value.requestedAt)).add(27, "day").toDate()
}

async function loadShippingAddress() {
  if (partner.value && partner.value.supplierType === "independent") {
    const data = await purchaseService.getOrderShippingAddress(Number(id.value))
    shippingAddress.value = data
  }
}

onMounted(async () => {
  try {
    await loadOrder()
    await loadShippingAddress()
  } catch (error) {
    console.log(error)
    reportSentry(error)
    router.replace("/orders")
  }
})

const isSubmitting = ref(false)

async function acceptOrder(productId: number) {
  isSubmitting.value = true

  const product = products.value.find((product) => product.id === productId)!
  const payload = {
    pickupAt: dayjs(product.pickupAt).toISOString(),
    offPrice: product.offPrice,
    deliveryMethod: product.deliveryMethod,
    shipper: product.shipper,
  }

  try {
    const data = await purchaseProductService.confirm(productId, payload)
    const orderData = await purchaseService.decide(Number(order.value!.id))

    product.checked = false
    product.status = data.status
    product.unitPrice = data.unitPrice
    product.price = data.price
    setPickupAtDue(product)

    order.value!.status = orderData.status
    store.dispatch("loadPartner", { force: true })

    isSubmitting.value = false
    const modal = bootstrap.Modal.getInstance(document.getElementById(`order-accept-${product.id}`))
    if (modal) modal.hide()
  } catch (err) {
    console.log(err)
    reportSentry(err)
    isSubmitting.value = false
  }
}

async function rejectOrder(productId: number) {
  isSubmitting.value = true

  const product = products.value.find((product) => product.id === productId)!
  const payload = {
    failureReason: product.failureReason!,
    extendedLeadtime: product.extendedLeadtime,
    stockQuantity: product.stockQuantity,
    risenUnitPrice: product.risenUnitPrice,
    productionMoq: product.productionMoq,
    productionLeadtime: product.productionLeadtime,
  }

  try {
    const data = await purchaseProductService.fail(productId, payload)
    const orderData = await purchaseService.decide(Number(order.value!.id))

    product.checked = false
    product.status = data.status
    order.value!.status = orderData.status
    store.dispatch("loadPartner", { force: true })

    isSubmitting.value = false
    const modal = bootstrap.Modal.getInstance(document.getElementById(`order-reject-${product.id}`))
    if (modal) modal.hide()
  } catch (err) {
    console.log(err)
    reportSentry(err)
    isSubmitting.value = false
  }
}

const checkAllRef = ref<HTMLInputElement | null>(null)

function toggleCheckAllItems() {
  const all = productsPending.value.length
  const checked = productsPending.value.filter((e) => e.checked).length
  if (checked < all)
    productsPending.value.forEach((product) => {
      product.checked = true
    })
  else
    productsPending.value.forEach((product) => {
      product.checked = false
    })
}

watchEffect(() => {
  const all = productsPending.value.length
  const checked = productsPending.value.filter((e) => e.checked).length

  if (checkAllRef.value) {
    if (all === checked) {
      checkAllRef.value.indeterminate = false
      checkAllRef.value.checked = true
    } else if (checked === 0) {
      checkAllRef.value.indeterminate = false
      checkAllRef.value.checked = false
    } else {
      checkAllRef.value.indeterminate = true
    }
  }
})

const isSwatchModalOpen = ref<Record<string, boolean>>({})

function onSwatchModalShown(id: number) {
  isSwatchModalOpen.value = { ...isSwatchModalOpen.value, [id]: true }
}
function onSwatchModalHidden(id: number) {
  isSwatchModalOpen.value = { ...isSwatchModalOpen.value, [id]: false }
}

const pickupAtSelect = ref<string | null>(null)
const pickupAt = ref<string | null>(null)
const failureReason = ref(null)
const extendedLeadtime = ref(null)
const stockQuantity = ref(null)
const risenUnitPrice = ref(null)
const productionMoq = ref(null)
const productionLeadtime = ref(null)

watchEffect(() => {
  if (pickupAtSelect.value === "today") {
    pickupAt.value = simpleFormatLocalDate(new Date())
  } else if (pickupAtSelect.value === "tomorrow") {
    pickupAt.value = simpleFormatLocalDate(dayjs(new Date()).add(1, "day").toDate())
  }
})

async function acceptAllSelected() {
  const productsChecked = productsPending.value.filter((e) => e.checked)
  for (const product of productsChecked) {
    product.pickupAt = dayjs(pickupAt.value!).toDate()
    await acceptOrder(product.id)
  }
  const modal = bootstrap.Modal.getInstance(document.getElementById(`order-accept-all`))
  if (modal) modal.hide()
  const backdrop = document.querySelector(".modal-backdrop")
  if (backdrop) backdrop.remove()
}

async function rejectAllSelected() {
  const productsChecked = productsPending.value.filter((e) => e.checked)
  for (const product of productsChecked) {
    product.failureReason = failureReason.value
    product.extendedLeadtime = extendedLeadtime.value
    product.stockQuantity = stockQuantity.value
    product.risenUnitPrice = risenUnitPrice.value
    product.productionMoq = productionMoq.value
    product.productionLeadtime = productionLeadtime.value
    await rejectOrder(product.id)
  }
  const modal = bootstrap.Modal.getInstance(document.getElementById(`order-reject-all`))
  if (modal) modal.hide()
  const backdrop = document.querySelector(".modal-backdrop")
  if (backdrop) backdrop.remove()
}

async function cancelOrder(productId: number) {
  isSubmitting.value = true

  const product = products.value.find((product) => product.id === productId)!
  const payload = {
    failureReason: product.failureReason!,
    extendedLeadtime: product.extendedLeadtime,
    stockQuantity: product.stockQuantity,
    risenUnitPrice: product.risenUnitPrice,
    productionMoq: product.productionMoq,
    productionLeadtime: product.productionLeadtime,
  }

  try {
    const data = await purchaseProductService.fail(productId, payload)

    product.checked = false
    product.status = data.status
    order.value!.status = data.purchase.status

    isSubmitting.value = false
    const modal = bootstrap.Modal.getInstance(document.getElementById(`order-cancel-${product.id}`))
    if (modal) modal.hide()
  } catch (err) {
    console.log(err)
    reportSentry(err)
    isSubmitting.value = false
  }
}

async function changeDate(productId: number) {
  isSubmitting.value = true

  const product = products.value.find((product) => product.id === productId)!
  const payload = {
    pickupAt: dayjs(product.newPickupAt!).toISOString(),
  }

  try {
    const data = await purchaseProductService.updatePickupAt(productId, payload)

    product.pickupAt = data.pickupAt

    isSubmitting.value = false
    const modal = bootstrap.Modal.getInstance(document.getElementById(`order-change-date-${product.id}`))
    if (modal) modal.hide()
  } catch (err) {
    console.log(err)
    reportSentry(err)
    isSubmitting.value = false
  }
}
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <Alert v-if="order && productsPending.length > 0" color="danger" class="mb-4">
      <Icon icon="bell" /> {{ formatByNumber($t("order.guides.newOrderItems"), productsPending.length) }}
    </Alert>

    <Card v-if="partner && order">
      <CardTitle>{{ t("order.basicInfo") }}</CardTitle>
      <CardContent>
        <div class="row">
          <div class="col-12 col-lg-6 col-xxl-3 mb-2 mb-lg-0">
            <InfoTable>
              <InfoRow :name="$t('order.code')">{{ order.code }}</InfoRow>
              <InfoRow :name="$t('order.customerCode')">{{ order.customerCode }}</InfoRow>
              <InfoRow :name="$t('order.orderDate')">{{ formattedDate(order.requestedAt) }}</InfoRow>
              <InfoRow :name="$t('order.status')">
                <StatusBadge model="order" field="status" :value="order.status" />
              </InfoRow>
            </InfoTable>
          </div>
          <div class="col-12 col-lg-6 col-xxl-3">
            <InfoTable>
              <InfoRow :name="$t('order.amount')">{{ formattedMoney(amount, currency) }}</InfoRow>
              <InfoRow :name="$t('order.amountAccepted')">
                <span class="text-success">{{ formattedMoney(amountAccepted, currency) }}</span>
              </InfoRow>
              <InfoRow :name="$t('order.amountRejected')">
                <span class="text-danger">{{ formattedMoney(amountRejected, currency) }} </span>
              </InfoRow>
            </InfoTable>
          </div>
        </div>
      </CardContent>
      <template v-if="partner.supplierType === 'independent' && shippingAddress">
        <CardSubtitle>
          {{ t("order.shippingAddress") }}
        </CardSubtitle>
        <CardContent>
          <div class="row">
            <div class="col-12 col-lg-6 col-xxl-3 mb-2 mb-lg-0">
              <InfoTable>
                <InfoRow name="Incoterms">{{ shippingAddress.orderIncoTerms }}</InfoRow>
                <InfoRow name="Street 1">{{ shippingAddress.street1 }}</InfoRow>
                <InfoRow name="Street 2">{{ shippingAddress.street2 }}</InfoRow>
                <InfoRow name="Postal Code">{{ shippingAddress.postalCode }}</InfoRow>
                <InfoRow name="City">{{ shippingAddress.city }}</InfoRow>
                <InfoRow name="State">{{ shippingAddress.state }}</InfoRow>
                <InfoRow name="Country">{{ shippingAddress.country.name }}</InfoRow>
              </InfoTable>
            </div>
            <div class="col-12 col-lg-6 col-xxl-3">
              <InfoTable>
                <InfoRow name="Contact Name">{{ shippingAddress.recipientName }}</InfoRow>
                <InfoRow name="Contact Phone">
                  +{{ shippingAddress.country.callingCode }} {{ shippingAddress.phone }}
                </InfoRow>
                <InfoRow name="Tax ID">{{ shippingAddress.taxId }}</InfoRow>
              </InfoTable>
            </div>
          </div>
        </CardContent>
      </template>
    </Card>

    <Card v-if="partner && order && products.length > 0">
      <CardTitle>{{ t("order.orderItems") }} ({{ products.length }})</CardTitle>
      <CardContent>
        <!-- 선택 상품에 대해 일괄 주문 수락 또는 거절 기능 -->
        <div v-if="order && order.status === 'requested'" class="d-flex mb-3">
          <input
            ref="checkAllRef"
            type="checkbox"
            class="form-check-input order-item-check"
            id="order-item-check-all"
            @click="toggleCheckAllItems()"
          />
          <u class="ms-3 text-success cursor-pointer" data-bs-toggle="modal" data-bs-target="#order-accept-all">
            {{ $t("order.actions.acceptAllSelected") }}
          </u>
          <u class="ms-3 text-danger cursor-pointer" data-bs-toggle="modal" data-bs-target="#order-reject-all">
            {{ $t("order.actions.rejectAllSelected") }}
          </u>
          <Modal modalId="order-accept-all" size="md" :title="$t('order.acceptOrder')">
            <form @submit.prevent="acceptAllSelected()">
              <div class="order-action d-flex flex-column">
                <div class="name-color d-flex align-items-end mb-2">
                  <b>
                    {{
                      formatByNumber(
                        $t("order.guides.forSelectedItems"),
                        productsPending.filter((e) => e.checked).length,
                      )
                    }}
                  </b>
                </div>
                <div
                  v-if="productsPending.filter((e) => e.checked && e.quantity >= 50).length > 0"
                  class="text-danger d-flex align-items-center mt-2 mb-2"
                >
                  <Icon icon="info-circle" class="me-2" />
                  <span>{{ $t("order.guides.rollPackagingRecommended") }}</span>
                </div>
                <InputRadio
                  v-model="pickupAtSelect"
                  :label="$t('order.pickupAt')"
                  field="pickupAtSelectAll"
                  :options="pickupAtSelectOptions"
                  required
                />
                <InputBox
                  class="mt-2"
                  v-if="pickupAtSelect === 'customDate'"
                  v-model="pickupAt"
                  type="date"
                  field="pickupAtAll"
                  :min="simpleFormatLocalDate(today)"
                  :max="dueStandard ? simpleFormatLocalDate(dueStandard) : undefined"
                  required
                />
                <Button
                  type="submit"
                  :text="$t('shared.accept')"
                  color="success"
                  class="mt-4"
                  :isSubmitting="isSubmitting"
                  :disabled="productsPending.filter((e) => e.checked).length === 0"
                />
              </div>
            </form>
          </Modal>
          <Modal modalId="order-reject-all" size="md" :title="$t('order.rejectOrder')">
            <form @submit.prevent="rejectAllSelected()">
              <div class="order-action d-flex flex-column">
                <div class="name-color d-flex align-items-end mb-2">
                  <b>
                    {{
                      formatByNumber(
                        $t("order.guides.forSelectedItems"),
                        productsPending.filter((e) => e.checked).length,
                      )
                    }}
                  </b>
                </div>
                <InputRadio
                  v-model="failureReason"
                  :label="$t('order.failureReason')"
                  field="failureReasonAll"
                  :options="failureReasonOptions"
                  required
                />
                <InputBox
                  v-if="failureReason === 'delayed'"
                  v-model="extendedLeadtime"
                  type="number"
                  :label="$t('order.extendedLeadtime')"
                  :guide="$t('order.guides.extendedLeadtime')"
                  field="extendedLeadtimeAll"
                  :suffix="$t('shared.units.days')"
                  :min="1"
                  class="mt-3"
                  required
                />
                <InputBox
                  v-if="failureReason === 'outOfStock'"
                  v-model="stockQuantity"
                  type="number"
                  :label="$t('order.stockQuantity')"
                  field="stockQuantityAll"
                  :suffix="$t('item.units.yards')"
                  :min="0"
                  class="mt-3"
                  required
                />
                <InputBox
                  v-if="failureReason === 'outOfStock'"
                  v-model="productionMoq"
                  type="number"
                  :label="$t('order.productionMoq')"
                  field="productionMoqAll"
                  :suffix="$t('item.units.yards')"
                  :min="0"
                  class="mt-3"
                  required
                />
                <InputBox
                  v-if="failureReason === 'outOfStock'"
                  v-model="productionLeadtime"
                  type="number"
                  :label="$t('order.productionLeadtime')"
                  field="productionLeadtimeAll"
                  :suffix="$t('shared.units.days')"
                  :min="0"
                  class="mt-3"
                  required
                />
                <InputBox
                  v-if="failureReason === 'unitPriceRisen'"
                  v-model="risenUnitPrice"
                  type="number"
                  :label="$t('order.risenUnitPrice')"
                  field="risenUnitPriceAll"
                  :suffix="currency.toUpperCase()"
                  :min="
                    Math.max(...productsPending.filter((e) => e.checked).map((e) => e.unitPrice || 0)) ||
                    minimumUnit(currency)
                  "
                  :step="minimumUnit(currency)"
                  class="mt-3"
                  required
                />
                <Button
                  type="submit"
                  :text="$t('shared.reject')"
                  color="danger"
                  class="mt-4"
                  :isSubmitting="isSubmitting"
                  :disabled="productsPending.filter((e) => e.checked).length === 0"
                />
              </div>
            </form>
          </Modal>
        </div>

        <!-- 주문 상품 리스트 -->
        <div
          class="py-3 order-item-row"
          :class="{ first: index === 0 }"
          v-for="(product, index) in products"
          :key="product.id"
        >
          <div class="row align-items-lg-center gx-3">
            <!-- 체크박스 -->
            <div v-if="order.status === 'requested'" class="col-12 col-lg-auto mb-2 mb-lg-0">
              <input
                type="checkbox"
                v-model="product.checked"
                class="form-check-input order-item-check"
                :disabled="product.status !== 'requested'"
              />
            </div>
            <!-- 이미지 -->
            <div class="col-auto">
              <img
                v-if="product.salesType === 'standard_order'"
                class="thumbnail cursor-pointer"
                :src="product.product.image?.small"
                data-bs-toggle="modal"
                :data-bs-target="`#image-view-${product.id}`"
              />
              <img
                v-if="product.salesType === 'print'"
                class="thumbnail cursor-pointer"
                :src="product.printingImage?.small"
                data-bs-toggle="modal"
                :data-bs-target="`#image-view-${product.id}`"
              />
              <Modal
                :modalId="`image-view-${product.id}`"
                size="md"
                @shown="onSwatchModalShown(product.id)"
                @hidden="onSwatchModalHidden(product.id)"
              >
                <SwatchColorView
                  v-if="product.salesType === 'standard_order'"
                  :product="product.product"
                  :modalOpen="!!isSwatchModalOpen[product.id]"
                />
                <PrintFileView v-if="product.salesType === 'print'" :purProduct="product" />
              </Modal>
            </div>
            <!-- 상품 정보 -->
            <div class="col">
              <div class="row align-items-center">
                <div class="col-12 col-lg-5 d-flex flex-column mb-3 mb-lg-0">
                  <b class="mb-1 mb-lg-2">{{ product.product.quality.name }}</b>
                  <span v-if="product.salesType === 'standard_order'">
                    {{ $t("colorProduct.colorCode") }} {{ product.product.colorCode }}
                  </span>
                  <span
                    v-if="product.salesType === 'print'"
                    class="print-file-link"
                    data-bs-toggle="modal"
                    :data-bs-target="`#image-view-${product.id}`"
                  >
                    {{ t("orderItem.printFile") }}
                  </span>
                </div>
                <div class="col d-flex flex-column flex-lg-row justify-content-between">
                  <span class="mb-1 mb-lg-0">{{ formattedMoney(product.unitPrice, currency) }}</span>
                  <span class="mb-2 mb-lg-0">{{ unitedNumber(product.quantity, $t("item.units.yards_short")) }}</span>
                  <b>{{ formattedMoney(product.price, currency) }}</b>
                </div>
              </div>
            </div>
            <!-- 주문 처리 기능 또는 상태 표시 -->
            <div class="col-12 col-lg-4 col-xxl-4">
              <!-- 주문 수락 상태 표시 -->
              <template v-if="product.status !== 'requested' && product.status !== 'failed'">
                <div class="d-flex flex-column align-items-end">
                  <template v-if="product.status === 'confirmed'">
                    <b class="text-success font-larger">{{ t("order.accepted") }}</b>
                    <span class="mt-1 text-end">
                      {{ t("order.pickupAt") }} <b>{{ formattedDate(product.pickupAt, false) }}</b>
                    </span>
                    <div>
                      <u
                        class="text-danger cursor-pointer"
                        data-bs-toggle="modal"
                        :data-bs-target="`#order-cancel-${product.id}`"
                      >
                        {{ t("order.cancel").toLowerCase() }}
                      </u>
                      |
                      <u
                        class="text-primary cursor-pointer"
                        data-bs-toggle="modal"
                        :data-bs-target="`#order-change-date-${product.id}`"
                      >
                        {{ t("order.changeDate").toLowerCase() }}
                      </u>
                    </div>
                    <Modal :modalId="`order-cancel-${product.id}`" size="lg" :title="$t('order.cancel')">
                      <form @submit.prevent="cancelOrder(product.id)">
                        <div class="order-action d-flex flex-column">
                          <div class="name-color d-flex align-items-end mb-2">
                            <b>{{ product.product.quality.name }}</b>
                            <small v-if="product.salesType === 'standard_order'" class="ms-2">
                              {{ $t("colorProduct.colorCode") }} {{ product.product.colorCode }}
                            </small>
                            <small v-if="product.salesType === 'print'" class="ms-2">
                              {{ $t("orderItem.salesTypeList.print") }}
                            </small>
                          </div>
                          <div class="row">
                            <div class="col-12 col-lg-6 col-xxl-6">
                              <img
                                v-if="product.salesType === 'standard_order'"
                                class="image mb-3"
                                :src="product.product.image?.large"
                              />
                              <img
                                v-if="product.salesType === 'print'"
                                class="image mb-3"
                                :src="product.printingImage?.large"
                              />
                            </div>
                            <div class="col-12 col-lg-6 col-xxl-6 d-flex flex-column">
                              <InfoTable class="mb-1">
                                <InfoRow :name="$t('order.priceQuantity')">
                                  {{ formattedMoney(product.unitPrice, currency) }} /
                                  {{ unitedNumber(product.quantity, $t("item.units.yards_short")) }}
                                </InfoRow>
                                <InfoRow :name="$t('order.amount')">
                                  {{ formattedMoney(product.price, currency) }}
                                </InfoRow>
                              </InfoTable>
                              <hr class="mt-0" />
                              <InputRadio
                                v-model="product.failureReason"
                                :label="$t('order.failureReason')"
                                field="failureReason"
                                :options="failureReasonOptions"
                                required
                              />
                              <InputBox
                                v-if="product.failureReason === 'delayed'"
                                v-model="product.extendedLeadtime"
                                type="number"
                                :label="$t('order.extendedLeadtime')"
                                :guide="$t('order.guides.extendedLeadtime')"
                                field="extendedLeadtime"
                                :suffix="$t('shared.units.days')"
                                :min="1"
                                class="mt-3"
                                required
                              />
                              <InputBox
                                v-if="product.failureReason === 'outOfStock'"
                                v-model="product.stockQuantity"
                                type="number"
                                :label="$t('order.stockQuantity')"
                                field="stockQuantity"
                                :suffix="$t('item.units.yards')"
                                :min="0"
                                class="mt-3"
                                required
                              />
                              <InputBox
                                v-if="product.failureReason === 'outOfStock'"
                                v-model="product.productionMoq"
                                type="number"
                                :label="$t('order.productionMoq')"
                                field="productionMoq"
                                :suffix="$t('item.units.yards')"
                                :min="0"
                                class="mt-3"
                              />
                              <InputBox
                                v-if="product.failureReason === 'outOfStock'"
                                v-model="product.productionLeadtime"
                                type="number"
                                :label="$t('order.productionLeadtime')"
                                field="productionLeadtime"
                                :suffix="$t('shared.units.days')"
                                :min="0"
                                class="mt-3"
                              />
                              <InputBox
                                v-if="product.failureReason === 'unitPriceRisen'"
                                v-model="product.risenUnitPrice"
                                type="number"
                                :label="$t('order.risenUnitPrice')"
                                field="risenUnitPrice"
                                :suffix="currency.toUpperCase()"
                                :min="product.unitPrice || minimumUnit(currency)"
                                :step="minimumUnit(currency)"
                                class="mt-3"
                                required
                              />
                              <Button
                                type="submit"
                                :text="$t('order.cancel')"
                                color="danger"
                                class="mt-4"
                                :isSubmitting="isSubmitting"
                              />
                            </div>
                          </div>
                        </div>
                      </form>
                    </Modal>
                    <Modal :modalId="`order-change-date-${product.id}`" size="lg" :title="$t('order.changeDate')">
                      <form @submit.prevent="changeDate(product.id)">
                        <div class="order-action d-flex flex-column">
                          <div class="name-color d-flex align-items-end mb-2">
                            <b>{{ product.product.quality.name }}</b>
                            <small v-if="product.salesType === 'standard_order'" class="ms-2">
                              {{ $t("colorProduct.colorCode") }} {{ product.product.colorCode }}
                            </small>
                            <small v-if="product.salesType === 'print'" class="ms-2">
                              {{ $t("orderItem.salesTypeList.print") }}
                            </small>
                          </div>
                          <div class="row">
                            <div class="col-12 col-lg-6 col-xxl-6">
                              <img
                                v-if="product.salesType === 'standard_order'"
                                class="image mb-3"
                                :src="product.product.image?.large"
                              />
                              <img
                                v-if="product.salesType === 'print'"
                                class="image mb-3"
                                :src="product.printingImage?.large"
                              />
                            </div>
                            <div class="col-12 col-lg-6 col-xxl-6 d-flex flex-column">
                              <InfoTable class="mb-1">
                                <InfoRow :name="$t('order.priceQuantity')">
                                  {{ formattedMoney(product.unitPrice, currency) }} /
                                  {{ unitedNumber(product.quantity, $t("item.units.yards_short")) }}
                                </InfoRow>
                                <InfoRow :name="$t('order.amount')">
                                  {{ formattedMoney(product.price, currency) }}
                                </InfoRow>
                                <InfoRow :name="$t('order.deliveryMethod')">
                                  {{ $t(`order.deliveryMethodList.${product.deliveryMethod}`) }}
                                </InfoRow>
                                <InfoRow :name="$t('order.pickupAtDue')">
                                  {{
                                    product.newPickupAtDue ? formatLocalDate(product.newPickupAtDue, false) : undefined
                                  }}
                                </InfoRow>
                              </InfoTable>
                              <div
                                v-if="product.quantity >= 50"
                                class="text-danger d-flex align-items-center mt-2 mb-2"
                              >
                                <Icon icon="info-circle" class="me-2" />
                                <span>{{ $t("order.guides.rollPackagingRecommended") }}</span>
                              </div>
                              <InputBox
                                class="mt-2"
                                :label="$t('order.newPickupAt')"
                                v-model="product.newPickupAt"
                                type="date"
                                field="newPickupAt"
                                :min="simpleFormatLocalDate(today)"
                                :max="
                                  product.newPickupAtDue ? simpleFormatLocalDate(product.newPickupAtDue) : undefined
                                "
                                required
                              />
                              <Button
                                type="submit"
                                :text="$t('shared.save')"
                                color="success"
                                class="mt-4"
                                :isSubmitting="isSubmitting"
                              />
                            </div>
                          </div>
                        </div>
                      </form>
                    </Modal>
                  </template>
                  <template v-else>
                    <b class="text-primary font-larger">{{ t("order.shippedOut") }}</b>
                  </template>
                </div>
              </template>
              <!-- 주문 실패 상태 표시 -->
              <template v-if="product.status === 'failed'">
                <div class="d-flex flex-column align-items-end">
                  <b class="text-danger font-larger">{{ t("order.rejected") }}</b>
                  <span class="mt-1 text-end">{{ $t(`order.failureReasonOptions.${product.failureReason}`) }}</span>
                </div>
              </template>
              <!-- 주문 처리 기능 -->
              <template v-if="product.status === 'requested'">
                <div class="row gx-2 justify-content-lg-end mt-3 mt-lg-0">
                  <div class="col col-lg-auto">
                    <Button
                      class="w-100"
                      :text="$t('shared.accept')"
                      color="outline-success"
                      :modal="`order-accept-${product.id}`"
                    />
                  </div>
                  <div class="col col-lg-auto">
                    <Button
                      class="w-100"
                      :text="$t('shared.reject')"
                      color="outline-danger"
                      :modal="`order-reject-${product.id}`"
                    />
                  </div>
                </div>
                <Modal :modalId="`order-accept-${product.id}`" size="lg" :title="$t('order.acceptOrder')">
                  <form @submit.prevent="acceptOrder(product.id)">
                    <div class="order-action d-flex flex-column">
                      <div class="name-color d-flex align-items-end mb-2">
                        <b>{{ product.product.quality.name }}</b>
                        <small v-if="product.salesType === 'standard_order'" class="ms-2">
                          {{ $t("colorProduct.colorCode") }} {{ product.product.colorCode }}
                        </small>
                        <small v-if="product.salesType === 'print'" class="ms-2">
                          {{ $t("orderItem.salesTypeList.print") }}
                        </small>
                      </div>
                      <div class="row">
                        <div class="col-12 col-lg-6 col-xxl-6">
                          <img
                            v-if="product.salesType === 'standard_order'"
                            class="image mb-3"
                            :src="product.product.image?.large"
                          />
                          <img
                            v-if="product.salesType === 'print'"
                            class="image mb-3"
                            :src="product.printingImage?.large"
                          />
                        </div>
                        <div class="col-12 col-lg-6 col-xxl-6 d-flex flex-column mt-4 mt-lg-0">
                          <InfoTable class="mb-1">
                            <InfoRow :name="$t('order.priceQuantity')">
                              {{ formattedMoney(product.unitPrice, currency) }} /
                              {{ unitedNumber(product.quantity, $t("item.units.yards_short")) }}
                            </InfoRow>
                            <InfoRow :name="$t('order.amount')">{{ formattedMoney(product.price, currency) }}</InfoRow>
                            <InfoRow :name="$t('order.deliveryMethod')">
                              {{ $t(`order.deliveryMethodList.${product.deliveryMethod}`) }}
                            </InfoRow>
                            <InfoRow :name="$t('order.pickupAtDue')">
                              {{ product.pickupAtDue ? formatLocalDate(product.pickupAtDue, false) : null }}
                            </InfoRow>
                          </InfoTable>
                          <div v-if="product.quantity >= 50" class="text-danger d-flex align-items-center mt-2 mb-2">
                            <Icon icon="info-circle" class="me-2" />
                            <span>{{ $t("order.guides.rollPackagingRecommended") }}</span>
                          </div>
                          <hr class="mt-0" />
                          <InputRadio
                            v-if="partner.supplierType === 'uncontrolled_ddm'"
                            v-model="product.deliveryMethod"
                            :label="$t('order.deliveryMethod')"
                            field="deliveryMethod"
                            :options="deliveryMethodOptions"
                            required
                          />
                          <InputCheck
                            v-if="partner.supplierType === 'uncontrolled_ddm'"
                            v-model="product.priceOffered"
                            :label="t('orderItem.priceOffer')"
                            field="priceOffered"
                            :options="[{ label: t('orderItem.offerOffPrice'), value: 'true' }]"
                          />
                          <InputBox
                            v-if="product.priceOffered"
                            v-model="product.offPrice"
                            :label="t('orderItem.offPrice')"
                            type="number"
                            field="offPrice"
                            :value="product.unitPrice"
                            :max="product.unitPrice || undefined"
                            :required="product.priceOffered"
                          />
                          <i v-if="product.offPrice" class="mt-1 mb-3">
                            {{ t("order.amount") }} {{ formattedNumber(product.offPrice) }} x
                            {{ formattedNumber(product.quantity) }} =
                            {{ formattedNumber(product.offPrice * product.quantity) }}
                          </i>
                          <InputRadio
                            v-model="product.pickupAtSelect"
                            :label="$t('order.pickupAt')"
                            field="pickupAtSelect"
                            :options="pickupAtSelectOptions"
                            required
                          />
                          <InputBox
                            class="mt-2"
                            v-if="product.pickupAtSelect === 'customDate'"
                            v-model="product.pickupAt"
                            type="date"
                            field="pickupAt"
                            :min="simpleFormatLocalDate(today)"
                            :max="product.pickupAtDue ? simpleFormatLocalDate(product.pickupAtDue) : undefined"
                            required
                          />
                          <InputBox
                            v-if="product.deliveryMethod === 'logistics'"
                            type="text"
                            v-model="product.shipper"
                            :label="$t('shipOut.shipper')"
                            field="shipper"
                            required
                          />
                          <Button
                            type="submit"
                            :text="$t('shared.accept')"
                            color="success"
                            class="mt-4"
                            :isSubmitting="isSubmitting"
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </Modal>
                <Modal :modalId="`order-reject-${product.id}`" size="lg" :title="$t('order.rejectOrder')">
                  <form @submit.prevent="rejectOrder(product.id)">
                    <div class="order-action d-flex flex-column">
                      <div class="name-color d-flex align-items-end mb-2">
                        <b>{{ product.product.quality.name }}</b>
                        <small v-if="product.salesType === 'standard_order'" class="ms-2">
                          {{ $t("colorProduct.colorCode") }} {{ product.product.colorCode }}
                        </small>
                        <small v-if="product.salesType === 'print'" class="ms-2">
                          {{ $t("orderItem.salesTypeList.print") }}
                        </small>
                      </div>
                      <div class="row">
                        <div class="col-12 col-lg-6 col-xxl-6">
                          <img
                            v-if="product.salesType === 'standard_order'"
                            class="image mb-3"
                            :src="product.product.image?.large"
                          />
                          <img
                            v-if="product.salesType === 'print'"
                            class="image mb-3"
                            :src="product.printingImage?.large"
                          />
                        </div>
                        <div class="col-12 col-lg-6 col-xxl-6 d-flex flex-column mt-4 mt-lg-0">
                          <InfoTable class="mb-1">
                            <InfoRow :name="$t('order.priceQuantity')">
                              {{ formattedMoney(product.unitPrice, currency) }} /
                              {{ unitedNumber(product.quantity, $t("item.units.yards_short")) }}
                            </InfoRow>
                            <InfoRow :name="$t('order.amount')">{{ formattedMoney(product.price, currency) }}</InfoRow>
                          </InfoTable>
                          <hr class="mt-0" />
                          <InputRadio
                            v-model="product.failureReason"
                            :label="$t('order.failureReason')"
                            field="failureReason"
                            :options="failureReasonOptions"
                            required
                          />
                          <InputBox
                            v-if="product.failureReason === 'delayed'"
                            v-model="product.extendedLeadtime"
                            type="number"
                            :label="$t('order.extendedLeadtime')"
                            :guide="$t('order.guides.extendedLeadtime')"
                            field="extendedLeadtime"
                            :suffix="$t('shared.units.days')"
                            :min="1"
                            class="mt-3"
                            required
                          />
                          <InputBox
                            v-if="product.failureReason === 'outOfStock'"
                            v-model="product.stockQuantity"
                            type="number"
                            :label="$t('order.stockQuantity')"
                            field="stockQuantity"
                            :suffix="$t('item.units.yards')"
                            :min="0"
                            class="mt-3"
                            required
                          />
                          <InputBox
                            v-if="product.failureReason === 'outOfStock'"
                            v-model="product.productionMoq"
                            type="number"
                            :label="$t('order.productionMoq')"
                            field="productionMoq"
                            :suffix="$t('item.units.yards')"
                            :min="0"
                            class="mt-3"
                          />
                          <InputBox
                            v-if="product.failureReason === 'outOfStock'"
                            v-model="product.productionLeadtime"
                            type="number"
                            :label="$t('order.productionLeadtime')"
                            field="productionLeadtime"
                            :suffix="$t('shared.units.days')"
                            :min="0"
                            class="mt-3"
                          />
                          <InputBox
                            v-if="product.failureReason === 'unitPriceRisen'"
                            v-model="product.risenUnitPrice"
                            type="number"
                            :label="$t('order.risenUnitPrice')"
                            field="risenUnitPrice"
                            :suffix="currency.toUpperCase()"
                            :min="product.unitPrice || minimumUnit(currency)"
                            :step="minimumUnit(currency)"
                            class="mt-3"
                            required
                          />
                          <i
                            v-if="product.failureReason === 'unitPriceRisen' && product.risenUnitPrice"
                            class="mt-1 mb-3"
                          >
                            {{ t("order.amount") }} {{ formattedNumber(product.risenUnitPrice) }} x
                            {{ formattedNumber(product.quantity) }} =
                            {{ formattedNumber(product.risenUnitPrice * product.quantity) }}
                          </i>
                          <Button
                            type="submit"
                            :text="$t('shared.reject')"
                            color="danger"
                            class="mt-4"
                            :isSubmitting="isSubmitting"
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </Modal>
              </template>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped>
.order-item-row {
  border-bottom: 1px solid #ccc;
}

.order-item-row.first {
  border-top: 1px solid #ccc;
}

img.thumbnail {
  width: 75px;
  height: 75px;

  @media (max-width: 991.98px) {
    width: 150px;
    height: 150px;
  }
}

.order-item-check {
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin: 0;
}

.order-action {
  @media (max-width: 991.98px) {
    margin-bottom: 200px;
  }

  .name-color {
    font-size: 18px;
  }

  .image {
    width: 100%;
  }
}

.print-file-link {
  text-decoration: underline;
  color: var(--bs-primary);
  cursor: pointer;
}
</style>
